import { useCallback, useMemo, useState } from "react";
import { Brand, Category, ProductInfo } from "@/types/AppTypes";
import { setShouldFetchProducts } from "@/store/slices/sellerDashboardSlice";
import { useAppDispatch } from "@/store/hooks";
import { setBrandsMap, setCategoriesMap } from "@/store/slices/appSlice";

export const useCombineProducts = ({
  productsForDisplay,
  productInfos,
  brands,
  categories,
  setProductsForDisplay,
  dispatch,
}: {
  productsForDisplay: any[];
  productInfos: ProductInfo[];
  brands: Brand[];
  categories: Category[];
  setProductsForDisplay?: (products: any[]) => void;
  dispatch?: any;
}) => {
  const [wholeProducts, setWholeProducts] = useState<any[]>([]);

  const brandMap = useMemo(() => {
    if (!brands || !Array.isArray(brands) || brands.length === 0) return {};
    return Object.fromEntries(
      brands.map((b) => (b?.id ? [b.id, b] : [])).filter((entry) => entry.length)
    );
  }, [brands]);

  const categoryMap = useMemo(() => {
    if (!categories || !Array.isArray(categories) || categories.length === 0) return {};
    return Object.fromEntries(
      categories.map((c) => (c?.id ? [c.id, c] : [])).filter((entry) => entry.length)
    );
  }, [categories]);

  const productInfoMap = useMemo(() => {
    if (!productInfos || !Array.isArray(productInfos) || productInfos.length === 0) return {};
    return Object.fromEntries(
      productInfos.map((p) => (p?.id ? [p.id, p] : [])).filter((entry) => entry.length)
    );
  }, [productInfos]);

  const combineProducts = useCallback(() => {
    const combined =
      productsForDisplay?.map((product) => {
        const productInfo = productInfoMap[product.product_id];
        const brand = brandMap[productInfo?.brand_id];
        let category;

        if (productInfo?.category_id) {
          if (Array.isArray(productInfo.category_id)) {
            category = productInfo.category_id
              .map((catId: string) => categoryMap[catId])
              .filter(Boolean);
          } else {
            let category = categoryMap[productInfo.category_id];
            category = category ? [category] : [];
          }
        } else {
          category = [];
        }
        if (dispatch) {
          dispatch(setCategoriesMap(categoryMap));
          dispatch(setBrandsMap(brandMap));
        }

        return {
          ...product,
          brand,
          category,
        };
      }) || [];

    if (dispatch && setProductsForDisplay) {
      dispatch(setProductsForDisplay(combined));
      dispatch(setShouldFetchProducts(false));
    }
    setWholeProducts(combined);
    //eslint-disable-next-line
  }, [productsForDisplay, productInfoMap, brandMap, categoryMap]);

  return { combineProducts, wholeProducts };
};
